import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeftIcon, ChevronRightIcon, XIcon,
  ClockIcon, MapPinIcon, UsersIcon, TagIcon, ArrowRightIcon,
} from '../common/Icons';

const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const DAYS_FR   = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const DAYS_FULL = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

const STATUS_COLORS = {
  planned:   { bg: '#dbeafe', text: '#1e40af', dot: '#3b82f6', label: 'Planifiée' },
  ongoing:   { bg: '#d1fae5', text: '#065f46', dot: '#10b981', label: 'En cours' },
  completed: { bg: '#f0fdf4', text: '#166534', dot: '#22c55e', label: 'Terminée' },
  cancelled: { bg: '#fef2f2', text: '#b91c1c', dot: '#ef4444', label: 'Annulée' },
};

function pad(n) { return String(n).padStart(2, '0'); }
function dateStr(y, m, d) { return `${y}-${pad(m + 1)}-${pad(d)}`; }

/* Convertit getDay() JS → index 0=Lun..6=Dim */
function jsToMyDay(jsDay) { return jsDay === 0 ? 6 : jsDay - 1; }

/**
 * Étend les activités récurrentes en occurrences virtuelles pour un mois donné.
 * Les activités non récurrentes sont retournées telles quelles.
 */
function expandActivities(activities, year, month) {
  const result = [];
  const monthStart = new Date(year, month, 1);
  const monthEnd   = new Date(year, month + 1, 0);

  activities.forEach(act => {
    // Activité ponctuelle sur plusieurs jours (end_date > date)
    if (!act.is_recurring && act.end_date && act.end_date > act.date) {
      const from = new Date(act.date);
      const to   = new Date(act.end_date);
      const clampFrom = from > monthStart ? from : monthStart;
      const clampTo   = to   < monthEnd   ? to   : monthEnd;
      for (let d = new Date(clampFrom); d <= clampTo; d.setDate(d.getDate() + 1)) {
        result.push({
          ...act,
          date: dateStr(d.getFullYear(), d.getMonth(), d.getDate()),
          _multiday: true,
          _firstDay: dateStr(from.getFullYear(), from.getMonth(), from.getDate()),
          _lastDay:  dateStr(to.getFullYear(), to.getMonth(), to.getDate()),
        });
      }
      return;
    }

    if (!act.is_recurring || !act.recurrence_days?.length) {
      result.push(act);
      return;
    }

    const startDate = new Date(act.date);
    // Si pas de fin, on étend sur 10 ans
    const endDate = act.recurrence_end ? new Date(act.recurrence_end) : new Date(year + 10, 11, 31);

    const from = startDate > monthStart ? startDate : monthStart;
    const to   = endDate   < monthEnd   ? endDate   : monthEnd;

    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
      const dayIdx = jsToMyDay(d.getDay());
      if (act.recurrence_days.includes(dayIdx)) {
        result.push({
          ...act,
          date: dateStr(d.getFullYear(), d.getMonth(), d.getDate()),
          _virtual: true,
        });
      }
    }
  });

  return result;
}

export default function ActivityCalendar({ activities, canEdit, onEdit, onDelete, onToggleStatus }) {
  const navigate = useNavigate();
  const today = new Date();
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });

  const prevMonth = useCallback(() => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  }, [month]);

  const nextMonth = useCallback(() => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  }, [month]);

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay    = (() => { const d = new Date(year, month, 1).getDay(); return d === 0 ? 6 : d - 1; })();

  // Expandre les récurrences pour ce mois
  const expanded = expandActivities(activities, year, month);

  // Regrouper par date
  const actsByDate = {};
  expanded.forEach(act => {
    if (!act.date) return;
    if (!actsByDate[act.date]) actsByDate[act.date] = [];
    actsByDate[act.date].push(act);
  });

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const handleEventClick = (e, act) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const popupW = 310;
    let left = rect.left + window.scrollX;
    if (left + popupW > window.innerWidth - 16) left = window.innerWidth - popupW - 16;
    if (left < 8) left = 8;
    setPopupPos({ top: rect.bottom + window.scrollY + 6, left });
    setSelectedEvent(act);
  };

  const sc = selectedEvent ? (STATUS_COLORS[selectedEvent.status] || STATUS_COLORS.planned) : null;

  return (
    <div style={{ position: 'relative' }}>
      {/* ── Header navigation ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button style={s.navBtn} onClick={prevMonth}><ChevronLeftIcon size={18} /></button>
          <span style={s.monthLabel}>{MONTHS_FR[month]} {year}</span>
          <button style={s.navBtn} onClick={nextMonth}><ChevronRightIcon size={18} /></button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* Légende récurrence */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem', color: '#16a34a', fontWeight: 600 }}>
            <span style={{ fontSize: '0.9rem' }}>↻</span> Récurrent
          </div>
          <button style={s.todayBtn} onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth()); }}>
            Aujourd'hui
          </button>
        </div>
      </div>

      {/* ── En-têtes jours ── */}
      <div style={s.dayHeaders}>
        {DAYS_FR.map(d => <div key={d} style={s.dayHeaderCell}>{d}</div>)}
      </div>

      {/* ── Grille ── */}
      <div style={s.grid} onClick={() => setSelectedEvent(null)}>
        {cells.map((day, idx) => {
          if (day === null) return <div key={`e-${idx}`} style={s.emptyCell} />;

          const ds = dateStr(year, month, day);
          const dayActs = actsByDate[ds] || [];
          const isToday = ds === dateStr(today.getFullYear(), today.getMonth(), today.getDate());
          const isPast  = new Date(year, month, day) < new Date(today.getFullYear(), today.getMonth(), today.getDate());

          return (
            <div key={ds} style={{ ...s.cell, backgroundColor: isToday ? '#eff6ff' : 'white', opacity: isPast && !isToday ? 0.72 : 1 }}>
              <div style={{ ...s.dayNum, ...(isToday ? s.todayNum : {}) }}>{day}</div>
              <div style={s.eventList}>
                {dayActs.slice(0, 3).map((act, i) => {
                  const c = STATUS_COLORS[act.status] || STATUS_COLORS.planned;
                  return (
                    <div key={`${act.id}-${i}`}
                      style={{ ...s.eventChip, backgroundColor: c.bg, color: c.text, borderLeft: `3px solid ${c.dot}` }}
                      onClick={e => handleEventClick(e, act)} title={act.name}>
                      {act.is_recurring && <span style={{ flexShrink: 0, fontSize: '0.65rem', marginRight: '2px' }}>↻</span>}
                      {act._multiday && <span style={{ flexShrink: 0, fontSize: '0.65rem', marginRight: '2px' }}>⟷</span>}
                      <span style={s.eventName}>{act.name}</span>
                      {act.start_time && <span style={s.eventTime}>{act.start_time.slice(0, 5)}</span>}
                    </div>
                  );
                })}
                {dayActs.length > 3 && (
                  <div style={s.moreChip}>+{dayActs.length - 3} autre{dayActs.length - 3 > 1 ? 's' : ''}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Popup détail ── */}
      {selectedEvent && sc && (
        <div style={{ ...s.popup, top: popupPos.top, left: popupPos.left }} onClick={e => e.stopPropagation()}>
          {/* Barre couleur */}
          <div style={{ height: '4px', backgroundColor: sc.dot, borderRadius: '4px 4px 0 0', margin: '-16px -16px 14px -16px' }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '0.97rem', color: '#1e293b', lineHeight: 1.3, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                {selectedEvent.name}
                {selectedEvent.is_recurring && (
                  <span style={{ fontSize: '0.62rem', padding: '2px 6px', background: '#dcfce7', color: '#16a34a', borderRadius: '999px', fontWeight: 700, flexShrink: 0 }}>↻ Récurrent</span>
                )}
              </div>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '0.72rem', fontWeight: 600, padding: '2px 8px', borderRadius: '999px', backgroundColor: sc.bg, color: sc.text }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: sc.dot, flexShrink: 0 }} />
                {sc.label}
              </span>
            </div>
            <button style={s.closeBtn} onClick={() => setSelectedEvent(null)}><XIcon size={16} /></button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', marginBottom: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ClockIcon size={14} color="#64748b" />
              <span style={{ fontSize: '0.83rem', color: '#374151' }}>
                {selectedEvent._firstDay || selectedEvent.date}
                {selectedEvent._multiday && selectedEvent._lastDay && ` → ${selectedEvent._lastDay}`}
                {selectedEvent.start_time && ` · ${selectedEvent.start_time.slice(0, 5)}`}
                {selectedEvent.end_time && ` - ${selectedEvent.end_time.slice(0, 5)}`}
              </span>
            </div>
            {selectedEvent.is_recurring && selectedEvent.recurrence_days?.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <span style={{ fontSize: '0.9rem', color: '#16a34a', lineHeight: 1.2 }}>↻</span>
                <span style={{ fontSize: '0.83rem', color: '#374151' }}>
                  Tous les {selectedEvent.recurrence_days.map(d => DAYS_FULL[d]).join(', ')}
                  {selectedEvent.recurrence_end
                    ? ` · jusqu'au ${selectedEvent.recurrence_end}`
                    : ' · sans fin'}
                </span>
              </div>
            )}
            {selectedEvent.location && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <MapPinIcon size={14} color="#64748b" />
                <span style={{ fontSize: '0.83rem', color: '#374151' }}>{selectedEvent.location}</span>
              </div>
            )}
            {selectedEvent.activity_type_detail?.name && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <TagIcon size={14} color="#64748b" />
                <span style={{ fontSize: '0.83rem', color: '#374151' }}>{selectedEvent.activity_type_detail.name}</span>
              </div>
            )}
            {selectedEvent.total_attendance !== undefined && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <UsersIcon size={14} color="#64748b" />
                <span style={{ fontSize: '0.83rem', color: '#374151' }}>{selectedEvent.total_attendance} présence{selectedEvent.total_attendance !== 1 ? 's' : ''}</span>
              </div>
            )}
          </div>

          {selectedEvent.description && (
            <div style={{ fontSize: '0.82rem', color: '#64748b', backgroundColor: '#f8fafc', borderRadius: '6px', padding: '8px 10px', marginBottom: '12px', lineHeight: 1.5 }}>
              {selectedEvent.description}
            </div>
          )}

          {/* Boutons action */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
            <button style={s.detailBtn} onClick={() => { navigate(`/activites/${selectedEvent.id}`); setSelectedEvent(null); }}>
              Voir le détail <ArrowRightIcon size={14} color="white" />
            </button>
            {canEdit && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px' }}>
                <button onClick={() => { onEdit && onEdit(selectedEvent); setSelectedEvent(null); }}
                  style={{ padding: '8px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, color: '#1e40af', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z"/></svg>
                  Modifier
                </button>
                <button onClick={() => { onToggleStatus && onToggleStatus(selectedEvent); setSelectedEvent(null); }}
                  style={{ padding: '8px', background: selectedEvent.status === 'cancelled' ? '#f0fdf4' : '#fef9c3', border: `1px solid ${selectedEvent.status === 'cancelled' ? '#bbf7d0' : '#fde68a'}`, borderRadius: '8px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, color: selectedEvent.status === 'cancelled' ? '#16a34a' : '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                  {selectedEvent.status === 'cancelled'
                    ? <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg> Activer</>
                    : <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg> Désactiver</>
                  }
                </button>
                <button onClick={() => { onDelete && onDelete(selectedEvent); setSelectedEvent(null); }}
                  style={{ padding: '8px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                  Supprimer
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {selectedEvent && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setSelectedEvent(null)} />
      )}
    </div>
  );
}

const s = {
  navBtn: { background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '7px', padding: '6px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#374151' },
  monthLabel: { fontSize: '1.1rem', fontWeight: 700, color: '#1e293b', minWidth: '160px', textAlign: 'center' },
  todayBtn: { background: 'white', border: '1px solid #d1d5db', borderRadius: '7px', padding: '6px 14px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: '#374151' },
  dayHeaders: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '4px' },
  dayHeaderCell: { textAlign: 'center', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', padding: '6px 0' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' },
  emptyCell: { minHeight: '90px', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #f1f5f9' },
  cell: { minHeight: '90px', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '3px', transition: 'background 0.15s' },
  dayNum: { fontSize: '0.82rem', fontWeight: 600, color: '#475569', lineHeight: 1, marginBottom: '2px', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' },
  todayNum: { backgroundColor: '#1e40af', color: 'white' },
  eventList: { display: 'flex', flexDirection: 'column', gap: '2px' },
  eventChip: { borderRadius: '4px', padding: '2px 5px', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '2px', whiteSpace: 'nowrap', overflow: 'hidden', transition: 'filter 0.15s' },
  eventName: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 },
  eventTime: { flexShrink: 0, opacity: 0.8 },
  moreChip: { fontSize: '0.68rem', color: '#64748b', fontWeight: 600, padding: '1px 4px' },
  popup: { position: 'fixed', zIndex: 200, backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', width: '310px', boxShadow: '0 8px 30px rgba(0,0,0,0.15)' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '2px', borderRadius: '4px', display: 'flex', alignItems: 'center', flexShrink: 0 },
  detailBtn: { width: '100%', backgroundColor: '#1e40af', color: 'white', border: 'none', borderRadius: '8px', padding: '9px 16px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' },
};
